import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ConfirmEndDialog from "../../../../../components/common/ConfirmationDialog/ConfirmEndDialog";
import { useSortableTable } from "../../../../../hooks/sorting/useSortableTable";
import SortableHeader from "../../../../../components/common/sorting/SortableHeader";
import { ResizeHandle, useColumnResize } from "../../../../../components/common/ResizeColumns";

const TYPE_MAP = {
  BIL: { label: "BILLED", bg: "rgba(40,179,180,0.12)", color: "#28B3B4" },
  UBL: { label: "UNBILLED", bg: "rgba(214,223,39,0.10)", color: "#D6DF27" },
  IMP: { label: "IMP", bg: "rgba(74,153,96,0.12)", color: "#4a9960" },
  ADM: { label: "ADM", bg: "rgba(122,90,189,0.12)", color: "#7a5abd" },
};

const getInitials = (name) => {
  if (!name) return "??";
  const parts = name.trim().split(" ");
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const AVATAR_COLORS = [
  "#28B3B4",
  "#D6DF27",
  "#4a9960",
  "#7a5abd",
  "#c88a3a",
  "#c84040",
];

const getAvatarColor = (name) => {
  if (!name) return AVATAR_COLORS[0];
  const code = name.charCodeAt(0) + (name.charCodeAt(1) || 0);
  return AVATAR_COLORS[code % AVATAR_COLORS.length];
};

const formatDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "2-digit",
  });
};

const DEFAULT_COL_WIDTHS          = [140, 100, 200, 70, 90, 90, 80, 190, 170, 160];
const DEFAULT_COL_WIDTHS_NO_BENCH = [140, 100, 200, 70, 90, 90, 80, 190, 160];

const thStyle = (t, align = "left") => ({
  padding: "10px 14px",
  textAlign: align,
  backgroundColor: t.surface,
  borderBottom: `1px solid ${t.border2}`,
  position: "sticky",
  top: 0,
  zIndex: 2,
  fontFamily: "'DM Mono', monospace",
  fontSize: "10px",
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: t.text3,
  fontWeight: 500,
});

const Avatar = ({ name, color }) => (
  <Box
    sx={{
      width: "28px",
      height: "28px",
      borderRadius: "50%",
      background: `${color}22`,
      border: `1px solid ${color}55`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontFamily: "'DM Mono', monospace",
      fontSize: "9px",
      fontWeight: 700,
      color,
      flexShrink: 0,
    }}
  >
    {getInitials(name)}
  </Box>
);

const tdStyle = {
  padding: "12px 14px",
  verticalAlign: "middle",
};

const POSITION_COMPARATORS = {
  allocatedTo: (a, b) => {
    const nameA =
      typeof a.allocatedTo === "string"
        ? a.allocatedTo
        : a.allocatedTo?.fullName ?? "";
    const nameB =
      typeof b.allocatedTo === "string"
        ? b.allocatedTo
        : b.allocatedTo?.fullName ?? "";
    return nameA.localeCompare(nameB, undefined, { sensitivity: "base" });
  },
};

const HEADER_COLS = [
  { label: "Position ID",       sortKey: "positionCode",        colIdx: 0 },
  { label: "Type",              sortKey: "positionType",         colIdx: 1 },
  { label: "Role",              sortKey: "roleTitle",            colIdx: 2 },
  { label: "Level",             sortKey: "levelCode",            colIdx: 3 },
  { label: "Start",             sortKey: "startDate",            colIdx: 4 },
  { label: "End",               sortKey: "endDate",              colIdx: 5 },
  { label: "Billed FTE",        sortKey: "billedFte",            colIdx: 6 },
  { label: "Allocated person",      sortKey: "allocatedTo",          colIdx: 6 },
  { label: "Benched Associate", sortKey: "benchedAssociateName", colIdx: 7 },
];

const PositionTable = ({ rows, loading, onEdit, onRemove, onView , activeFilter, readOnly = false }) => {
  const theme = useTheme();
  const t = theme.custom;

  const [confirmRow, setConfirmRow] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmError, setConfirmError] = useState(null);

  const showBenchCol = activeFilter !== "BIL";
  const { colWidths, buildGrid, getHandleProps } = useColumnResize(
    showBenchCol ? DEFAULT_COL_WIDTHS : DEFAULT_COL_WIDTHS_NO_BENCH
  );

  const { sortedRows, sortKey, sortDir, handleSort } = useSortableTable(rows, {
    defaultKey: "positionCode",
    defaultDir: "asc",
    comparators: POSITION_COMPARATORS,
  });

  const handleRemoveClick = (row) => {
    setConfirmRow(row);
    setConfirmError(null);
  };
  const handleCancel = () => {
    if (confirmLoading) return;
    setConfirmRow(null);
    setConfirmError(null);
  };
  const handleConfirm = async () => {
    try {
      setConfirmLoading(true);
      setConfirmError(null);
      await onRemove(confirmRow);
      setConfirmRow(null);
    } catch (err) {
      setConfirmError(err?.message || "Something went wrong. Please try again.");
    } finally {
      setConfirmLoading(false);
    }
  };

  const sharedHeaderProps = { activeSortKey: sortKey, sortDir, onSort: handleSort };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box sx={{ flex: 1, overflowX: "auto", overflowY: "auto" }}>
        <table
          style={{
            width: "100%",
            minWidth: `${colWidths.reduce((a, b) => a + b, 0)}px`,
            borderCollapse: "collapse",
            tableLayout: "fixed",
          }}
        >
          <colgroup>
            {colWidths.map((w, i) => (
              <col key={i} style={{ width: `${w}px` }} />
            ))}
          </colgroup>

          <thead>
            <tr>
              {HEADER_COLS.filter(({ sortKey: sk }) => showBenchCol || sk !== "benchedAssociateName").map(({ label, sortKey: sk, colIdx }) => (
                <th
                  key={sk}
                  style={{ ...thStyle(t), position: "relative", userSelect: "none" }}
                >
                  <SortableHeader
                    label={label}
                    sortKey={sk}
                    style={{}}
                    {...sharedHeaderProps}
                  />
                  <ResizeHandle handleProps={getHandleProps(colIdx)} />
                </th>
              ))}
              <th style={{ ...thStyle(t, "right"), position: "relative", userSelect: "none" }}>
                Actions
                <ResizeHandle handleProps={getHandleProps(9)} />
              </th>
            </tr>
          </thead>

          <tbody>
            {loading &&
              [1, 2, 3, 4, 5].map((i) => (
                <tr key={i}>
                  {DEFAULT_COL_WIDTHS.map((w, j) => (
                    <td
                      key={j}
                      style={{
                        padding: "14px",
                        borderBottom: `1px solid ${t.border}`,
                      }}
                    >
                      <Box
                        sx={{
                          height: "12px",
                          borderRadius: "3px",
                          width: `${w}px`,
                          maxWidth: "100%",
                          background: t.surface3,
                          opacity: 0.6,
                        }}
                      />
                    </td>
                  ))}
                </tr>
              ))}

            {!loading && sortedRows.length === 0 && (
              <tr>
                <td colSpan={9} style={{ padding: "60px 20px", textAlign: "center" }}>
                  <Typography
                    sx={{
                      fontFamily: "'DM Mono', monospace",
                      fontSize: "12px",
                      color: t.text3,
                      letterSpacing: "0.08em",
                    }}
                  >
                    No positions found
                  </Typography>
                </td>
              </tr>
            )}

            {!loading &&
              sortedRows.map((row, i) => {
                const typeCfg = TYPE_MAP[row.positionType] || TYPE_MAP.BIL;
                const isLast = i === sortedRows.length - 1;

                const allocatee =
                  typeof row.allocatedTo === "string"
                    ? {
                        fullName: row.allocatedTo,
                        macName: row.allocatedToMac ?? null,
                        fte: row.fte ?? null,
                      }
                    : row.allocatedTo;

                const hasAllocatee = allocatee?.fullName;
                const isBench = row.ublStatus === "BENCH";
                const avatarColor = hasAllocatee
                  ? getAvatarColor(allocatee.fullName)
                  : "#28B3B4";

                const isFilledUblSlot =
                  row.positionType === "UBL" && row.benchedAssociateName;

                const canRemove = !hasAllocatee && !isFilledUblSlot;

                return (
                  <tr
                    key={row.positionCode}
                    style={{
                      borderBottom: isLast ? "none" : `1px solid ${t.border}`,
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = t.surface2)
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "transparent")
                    }
                  >
                    {/* Position ID */}
                    <td style={tdStyle}>
                      <Typography
                        onClick={() => onView?.(row)}
                        sx={{
                          fontFamily: "'DM Mono', monospace",
                          fontSize: "12px",
                          color: t.accent,
                          fontWeight: 500,
                          cursor: "pointer",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        {row.positionCode}
                      </Typography>
                      {row.isExtra && (
                        <Box
                          sx={{
                            display: "inline-flex",
                            background: "rgba(200,64,64,0.10)",
                            borderRadius: "3px",
                            padding: "1px 6px",
                            mt: "3px",
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: "'DM Mono', monospace",
                              fontSize: "8px",
                              letterSpacing: "0.1em",
                              textTransform: "uppercase",
                              color: "#c84040",
                              fontWeight: 600,
                            }}
                          >
                            Temp
                          </Typography>
                        </Box>
                      )}
                    </td>

                    {/* Type */}
                    <td style={tdStyle}>
                      <Box
                        sx={{
                          display: "inline-flex",
                          background: typeCfg.bg,
                          borderRadius: "3px",
                          padding: "2px 8px",
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: "'DM Mono', monospace",
                            fontSize: "9px",
                            letterSpacing: "0.1em",
                            textTransform: "uppercase",
                            color: typeCfg.color,
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {typeCfg.label}
                        </Typography>
                      </Box>
                    </td>

                    {/* Role + SOW */}
                    <td style={tdStyle}>
                      <Typography
                        sx={{
                          fontFamily: "'Inter', sans-serif",
                          fontSize: "13px",
                          fontWeight: 600,
                          color: t.text,
                          lineHeight: 1.2,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.roleTitle || "—"}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: "'Inter', sans-serif",
                          fontSize: "11px",
                          color: t.text3,
                          mt: "1px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.sowName || "—"}
                      </Typography>
                    </td>

                    {/* Level */}
                    <td style={tdStyle}>
                      <Typography
                        sx={{
                          fontFamily: "'Inter', sans-serif",
                          fontSize: "13px",
                          fontWeight: 600,
                          color: t.text,
                        }}
                      >
                        {row.levelCode || "—"}
                      </Typography>
                    </td>

                    {/* Start */}
                    <td style={tdStyle}>
                      <Typography
                        sx={{
                          fontFamily: "'DM Mono', monospace",
                          fontSize: "11px",
                          color: t.text2,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {formatDate(row.startDate)}
                      </Typography>
                    </td>

                    {/* End */}
                    <td style={tdStyle}>
                      <Typography
                        sx={{
                          fontFamily: "'DM Mono', monospace",
                          fontSize: "11px",
                          color: t.text2,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {formatDate(row.endDate ?? row.endedAt ?? null)}
                      </Typography>
                    </td>

                    {/* Billed FTE */}
<td style={tdStyle}>
  {row.positionType === "BIL" && row.billedFte != null ? (
    <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <Box
        sx={{
          width: `${Math.min(row.billedFte, 1) * 48}px`,
          height: "3px",
          borderRadius: "2px",
          background: row.billedFte >= 1 ? "#28B3B4" : "#D6DF27",
          flexShrink: 0,
        }}
      />
      <Typography
        sx={{
          fontFamily: "'DM Mono', monospace",
          fontSize: "11px",
          color: t.text2,
        }}
      >
        {row.billedFte.toFixed(1)}
      </Typography>
    </Box>
  ) : (
    <Typography
      sx={{
        fontFamily: "'DM Mono', monospace",
        fontSize: "12px",
        color: t.text3,
      }}
    >
      —
    </Typography>
  )}
</td>
                    {/* Allocated To */}
                    <td style={tdStyle}>
                      {hasAllocatee ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            minWidth: 0,
                          }}
                        >
                          <Avatar name={allocatee.fullName} color={avatarColor} />
                          <Box sx={{ minWidth: 0, overflow: "hidden" }}>
                            <Typography
                              sx={{
                                fontFamily: "'Inter', sans-serif",
                                fontSize: "12px",
                                fontWeight: 500,
                                color: t.text,
                                lineHeight: 1.2,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {allocatee.fullName}
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: "'Inter', sans-serif",
                                fontSize: "10.5px",
                                color: t.text3,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {allocatee.macName}
                              {allocatee.fte && (
                                <span style={{ color: t.text3 }}>
                                  {" "}· {allocatee.fte} FTE
                                </span>
                              )}
                            </Typography>
                          </Box>
                        </Box>
                      ) : (
                        <Typography
                          sx={{
                            fontFamily: "'Inter', sans-serif",
                            fontSize: "12px",
                            color: t.text3,
                            fontStyle: "italic",
                          }}
                        >
                          {isBench ? "— Bench Pool —" : "— Unfilled —"}
                        </Typography>
                      )}
                    </td>

                    {/* Benched Associate */}
                    {showBenchCol && <td style={tdStyle}>
                      {row.positionType === "UBL" ? (
                        row.benchedAssociateName ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              minWidth: 0,
                              overflow: "hidden",
                            }}
                          >
                            <Avatar
                              name={row.benchedAssociateName}
                              color={getAvatarColor(row.benchedAssociateName)}
                            />
                            <Typography
                              sx={{
                                fontFamily: "'Inter', sans-serif",
                                fontSize: "12px",
                                fontWeight: 500,
                                color: t.text,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {row.benchedAssociateName}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography
                            sx={{
                              fontFamily: "'Inter', sans-serif",
                              fontSize: "12px",
                              color: t.text3,
                              fontStyle: "italic",
                            }}
                          >
                            — Empty Slot —
                          </Typography>
                        )
                      ) : (
                        <Typography
                          sx={{
                            fontFamily: "'Inter', sans-serif",
                            fontSize: "12px",
                            color: t.text3,
                          }}
                        >
                          —
                        </Typography>
                      )}
                    </td>}

                    {/* Actions */}
                    <td style={{ ...tdStyle, textAlign: "right" }}>
                      <Box
                        sx={{
                          display: "flex",
                          gap: "6px",
                          justifyContent: "flex-end",
                        }}
                      >
                        {!isFilledUblSlot && (
  <>
    <Box
      onClick={() => onView?.(row)}
      sx={{
        padding: "4px 10px",
        borderRadius: "3px",
        cursor: "pointer",
        border: `1px solid ${t.border2}`,
        fontFamily: "'DM Mono', monospace",
        fontSize: "10px",
        color: t.text2,
        transition: "all 0.15s",
        whiteSpace: "nowrap",
        userSelect: "none",
        "&:hover": {
          borderColor: t.accent,
          color: t.accent,
          background: t.accent2,
        },
      }}
    >
      View
    </Box>
    {!readOnly && (
      <>
        <Box
          onClick={() => onEdit?.(row)}
          sx={{
            padding: "4px 10px",
            borderRadius: "3px",
            cursor: "pointer",
            border: `1px solid ${t.border2}`,
            fontFamily: "'DM Mono', monospace",
            fontSize: "10px",
            color: t.text2,
            transition: "all 0.15s",
            whiteSpace: "nowrap",
            userSelect: "none",
            "&:hover": {
              borderColor: t.accent,
              color: t.accent,
              background: t.accent2,
            },
          }}
        >
          Edit
        </Box>
        {canRemove && (
          <Box
            onClick={() => handleRemoveClick(row)}
            sx={{
              padding: "4px 10px",
              borderRadius: "3px",
              cursor: "pointer",
              background: "rgba(200,64,64,0.10)",
              border: "1px solid rgba(200,64,64,0.25)",
              fontFamily: "'DM Mono', monospace",
              fontSize: "10px",
              color: "#c84040",
              transition: "all 0.15s",
              whiteSpace: "nowrap",
              userSelect: "none",
              "&:hover": {
                background: "rgba(200,64,64,0.20)",
              },
            }}
          >
            Remove
          </Box>
        )}
      </>
    )}
  </>
)}
                      </Box>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </Box>

      <ConfirmEndDialog
        open={!!confirmRow}
        loading={confirmLoading}
        error={confirmError}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        title="End Position"
        confirmLabel="End Position"
        warningText="Are you sure you want to end this position? This will close the position and it will no longer be available for allocation."
        primaryText={confirmRow?.positionCode}
        badgeText={confirmRow?.positionType}
        subText={`${confirmRow?.roleTitle || "—"} · ${confirmRow?.sowName || "—"}`}
      />
    </Box>
  );
};

export default PositionTable;